import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X, Download, Building2, Lock } from "lucide-react";
import api, { apiError } from "../lib/api";
import { CommandCard } from "../components/CommandCard";
import { WelcomeTour } from "../components/WelcomeTour";
import { useSeo } from "../lib/useSeo";
import { useAuth } from "../context/AuthContext";
import TrialLockedNotice from "../components/TrialLockedNotice";

export default function Dictionary() {
  const { user } = useAuth();
  const isTrial = user && user.membership === "trial";
  const trialCats = (user && user.trial_categories) || [];
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [showAllCats, setShowAllCats] = useState(false);
  const [categories, setCategories] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadError, setLoadError] = useState("");
  const requestSequence = useRef(0);
  const [showTour, setShowTour] = useState(() => !localStorage.getItem("aish_tour_done"));
  const [params, setParams] = useSearchParams();
  const [industrySel, setIndustrySel] = useState(null);
  const [sugs, setSugs] = useState([]);
  const [showSugs, setShowSugs] = useState(false);
  const skipSug = useRef(false);
  const lastPhrase = useRef("");
  useSeo({ title: "Dictionary", noindex: true });

  useEffect(() => {
    const fromCat = params.get("category");
    if (fromCat) setCategory(fromCat);
    const fromQ = params.get("q");
    if (fromQ) setQ(fromQ);
    const fromUrl = params.get("industry");
    let pending = null;
    try { pending = JSON.parse(localStorage.getItem("aish_pending_industry") || "null"); } catch {}
    if (fromUrl) {
      if (pending && pending.industry === fromUrl) {
        setIndustrySel(pending);
      } else {
        api.get("/industries").then(({ data }) => {
          const ind = data.find((i) => i.slug === fromUrl);
          if (ind) {
            const sub = ind.subcategories.find((s) => s.slug === params.get("sub"));
            setIndustrySel({ industry: ind.slug, industryName: ind.name, sub: sub?.slug || "", subName: sub?.name || "" });
          }
        }).catch(() => {});
      }
      localStorage.removeItem("aish_pending_industry");
    } else if (fromCat) {
      // Drilling in from the Browse Categories page — honour the card's
      // promise (e.g. "20 shortcuts" must show 20 shortcuts). Drop any
      // sticky industry context that leaked in from an earlier session.
      localStorage.removeItem("aish_pending_industry");
    } else if (pending) {
      setIndustrySel(pending);
      localStorage.removeItem("aish_pending_industry");
    }
  }, []); // eslint-disable-line

  const clearIndustry = () => {
    setIndustrySel(null);
    setParams({}, { replace: true });
  };

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const load = useCallback(async (query, cat, industry, offset = 0) => {
    const request = ++requestSequence.current;
    setLoadError("");
    if (offset) setLoadingMore(true);
    else { setLoading(true); setLoadingMore(false); setResults([]); setTotal(0); }
    try {
      const { data, headers } = await api.get("/commands", { params: { q: query, category: cat, industry: industry || "", offset } });
      if (request !== requestSequence.current) return;
      setResults((previous) => offset ? [...previous, ...data.filter((item) => !previous.some((old) => old.slug === item.slug))] : data);
      setTotal(Number(headers["x-total-count"] ?? data.length));
      lastPhrase.current = query;
    } catch (error) { if (request === requestSequence.current) setLoadError(apiError(error)); }
    finally { if (request === requestSequence.current) { setLoading(false); setLoadingMore(false); } }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q, category, industrySel?.industry), q ? 300 : 0);
    return () => { clearTimeout(t); requestSequence.current += 1; };
  }, [q, category, industrySel, load]);

  const jumpTo = (slug) => { setCategory(""); setQ("/" + slug); window.scrollTo({ top: 0, behavior: "smooth" }); };

  useEffect(() => {
    if (skipSug.current) { skipSug.current = false; return; }
    const t = setTimeout(() => {
      api.get("/search-suggestions", { params: { q } }).then((r) => setSugs(r.data)).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const pickSug = (s) => {
    skipSug.current = true;
    setShowSugs(false);
    setQ(s.text);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {showTour && <WelcomeTour onDone={() => setShowTour(false)} />}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6">
        <div className="relative rounded-2xl overflow-hidden border-2 border-[#D4AF37] shadow-lg shadow-amber-900/10 aspect-video bg-black" data-testid="dictionary-howto-video">
          <iframe
            src="https://www.youtube.com/embed/90wHg4fOaXc"
            title="How to use AI Prompt Shortcuts"
            className="absolute inset-0 w-full h-full"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
        <p className="text-center text-xs text-slate-400 mt-2 mb-1">New here? Watch how to get perfect results in 3 clicks.</p>
      </div>
      <div className="sticky top-16 z-40 bg-[#FAFAF7]/90 backdrop-blur-md border-b border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {industrySel && (
            <div className="max-w-2xl mx-auto mb-3 flex items-center justify-between gap-3 bg-[#0B0F0D] text-white rounded-xl px-4 py-2.5" data-testid="industry-banner">
              <span className="flex items-center gap-2 text-sm min-w-0">
                <Building2 size={15} className="text-[#D4AF37] shrink-0" />
                <span className="truncate">Shortcuts for <strong>{industrySel.subName || industrySel.industryName}</strong>{industrySel.subName ? ` (${industrySel.industryName})` : ""}</span>
              </span>
              <button data-testid="clear-industry-btn" onClick={clearIndustry} className="text-gray-400 hover:text-white shrink-0 p-1" aria-label="Clear industry filter"><X size={15} /></button>
            </div>
          )}
          <form className="max-w-2xl mx-auto flex gap-2" onSubmit={(e) => { e.preventDefault(); setShowSugs(false); load(q, category, industrySel?.industry); }}>
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input data-testid="dictionary-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                onFocus={() => setShowSugs(true)} onBlur={() => setTimeout(() => setShowSugs(false), 150)}
                onKeyDown={(e) => { if (e.key === "Escape") setShowSugs(false); }}
                placeholder="What do you want AI to help you do?"
                className="w-full bg-white border-2 border-slate-200 focus:border-emerald-500 rounded-2xl pl-12 pr-10 py-3.5 text-base shadow-sm focus:outline-none transition-colors" />
              {q && (
                <button type="button" data-testid="clear-search-btn" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1">
                  <X size={18} />
                </button>
              )}
              {showSugs && sugs.length > 0 && (
                <div data-testid="search-suggestions" className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 py-1.5 overflow-hidden">
                  {!q.trim() && <p className="px-4 pt-1.5 pb-0.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Popular searches</p>}
                  {sugs.map((s, i) => (
                    <button key={`${s.type}-${s.text}`} type="button" data-testid={`search-suggestion-${i}`}
                      onMouseDown={(e) => { e.preventDefault(); pickSug(s); }}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-emerald-50 flex items-center gap-2.5 transition-colors">
                      {s.type === "command"
                        ? <span className="cmd-chip bg-slate-900 text-amber-400 font-bold px-2 py-0.5 rounded text-xs">{s.text}</span>
                        : <><Search size={13} className="text-slate-400 shrink-0" /><span className="text-slate-700">{s.text}</span></>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" data-testid="dictionary-search-btn"
              className="shrink-0 bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] text-white font-bold px-5 sm:px-7 rounded-2xl text-sm sm:text-base shadow-sm transition-all">
              Search
            </button>
          </form>
          <div className={`flex flex-wrap justify-center gap-2 mt-3 pb-1 max-w-5xl mx-auto overflow-hidden ${showAllCats ? "" : "max-h-[5.5rem]"}`} data-testid="category-chips">
            <button data-testid="category-chip-all" onClick={() => { setCategory(""); setQ(""); }}
              className={`shrink-0 rounded-full px-4 py-2 text-sm transition-all ${!category ? "bg-slate-900 text-white font-semibold shadow-sm" : "bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium"}`}>
              All
            </button>
            {categories.map((c) => {
              const locked = isTrial && !trialCats.includes(c.name);
              return (
              <button key={c.id} data-testid={`category-chip-${c.slug}`} onClick={() => { setCategory(category === c.name ? "" : c.name); setQ(""); }}
                className={`shrink-0 rounded-full px-4 py-2 text-sm transition-all inline-flex items-center gap-1.5 ${category === c.name ? "bg-slate-900 text-white font-semibold shadow-sm" : locked ? "bg-slate-50 border border-slate-200 text-slate-500 font-medium hover:bg-slate-100" : "bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium"}`}>
                {locked && <Lock size={11} className="opacity-70" />} {c.name}
              </button>
            );})}
          </div>
          <button onClick={() => setShowAllCats(!showAllCats)} data-testid="category-chips-toggle"
            className="mt-2 mx-auto block text-xs font-bold text-emerald-700 hover:text-emerald-800 transition-colors">
            {showAllCats ? "Show fewer categories ▲" : `Show all ${categories.length} categories ▼`}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {industrySel && category && (() => {
          const catTotal = categories.find((c) => c.name === category)?.count;
          return (
            <div data-testid="dual-filter-banner" className="mb-4 rounded-xl border-2 border-amber-200 bg-amber-50 px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
              <span className="font-bold text-amber-900">Showing:</span>
              <span data-testid="dual-filter-category" className="inline-flex items-center gap-1.5 bg-white border border-amber-300 text-amber-900 font-bold rounded-full px-3 py-1 text-xs">
                {category}
                <button onClick={() => setCategory("")} aria-label={`Clear category ${category}`}
                  className="text-amber-700 hover:text-rose-700" data-testid="dual-filter-clear-category">
                  <X size={12} />
                </button>
              </span>
              <span className="text-amber-800 font-bold">×</span>
              <span data-testid="dual-filter-industry" className="inline-flex items-center gap-1.5 bg-white border border-amber-300 text-amber-900 font-bold rounded-full px-3 py-1 text-xs">
                <Building2 size={11} /> {industrySel.subName || industrySel.industryName}
                <button onClick={clearIndustry} aria-label="Clear industry"
                  className="text-amber-700 hover:text-rose-700" data-testid="dual-filter-clear-industry">
                  <X size={12} />
                </button>
              </span>
              {typeof catTotal === "number" && (
                <span className="text-xs text-amber-900">
                  Showing <strong>{results.length}</strong> of <strong>{catTotal}</strong> in this category —
                  {" "}
                  <button onClick={clearIndustry} data-testid="dual-filter-show-all-in-category"
                    className="underline font-bold hover:text-amber-700">
                    see all {catTotal}
                  </button>
                </span>
              )}
            </div>
          );
        })()}
        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-slate-500" data-testid="results-count">
            {loading ? "Searching…" : `${results.length < total ? `${results.length} of ${total}` : total} command${total === 1 ? "" : "s"}${category ? ` in ${category}` : ""}`}
          </p>
          <a data-testid="cheatsheet-download-link" href={`${process.env.REACT_APP_BACKEND_URL}/api/cheatsheet`}
            className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-800">
            <Download size={15} /> Cheat Sheet
          </a>
        </div>
        {category && (() => {
          const cat = categories.find((c) => c.name === category);
          if (!cat?.disclaimer) return null;
          return (
            <div data-testid="category-disclaimer" className="mb-5 flex items-start gap-2.5 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-xs sm:text-sm text-amber-900 leading-relaxed">
              <span className="text-amber-700 font-black text-base leading-none mt-0.5">⚠</span>
              <span>{cat.disclaimer}</span>
            </div>
          );
        })()}
        {!loading && isTrial && category && !trialCats.includes(category) ? (
          <TrialLockedNotice categoryName={category} trialCategories={trialCats} />
        ) : (
          <>
            {!loading && !loadError && results.length === 0 && (
              <div className="text-center py-20" data-testid="no-results">
                <p className="font-display font-bold text-lg text-slate-700">No commands found</p>
                <p className="text-sm text-slate-400 mt-2">Try a simpler phrase like "advert", "email" or "research" — we log every search and add new commands based on demand.</p>
              </div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.map((c) => (
                <CommandCard key={c.id} cmd={c} searchPhrase={lastPhrase.current} onRelatedClick={jumpTo} />
              ))}
            </div>
            {loadError && <div role="alert" data-testid="dictionary-load-error" className="mt-5 text-sm text-red-700 text-center">
              <p>{loadError}</p>
              <button type="button" data-testid="dictionary-load-retry" onClick={() => load(q, category, industrySel?.industry, results.length)} className="mt-2 font-semibold underline">Try again</button>
            </div>}
            {!loading && results.length < total && <div className="mt-8 text-center">
              <button type="button" data-testid="dictionary-load-more" disabled={loadingMore} onClick={() => load(q, category, industrySel?.industry, results.length)}
                className="rounded-full border border-emerald-700 bg-white px-6 py-3 text-sm font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-50 transition-colors">
                {loadingMore ? "Loading…" : "Load more commands"}
              </button>
            </div>}
          </>
        )}
        <p data-testid="ai-disclaimer" className="text-xs text-slate-400 text-center mt-12 max-w-2xl mx-auto">
          AI-generated responses may contain errors or inaccuracies. Always verify important information before relying on it.
        </p>
      </div>
    </div>
  );
}
